import type { DemAppUser, DemAppUserRole, DemIntegrationConfig, DemSequenceConfig, DemSequenceKey } from '../types/dem';
import { formatSupabaseError } from './formatSupabaseError';
import type { DemEmailTemplates } from './demOrderNotifications';
import { getDbSupabase } from './supabase';

const SEQUENCE_DEFAULTS: Record<DemSequenceKey, { prefix: string }> = {
  po_sequence: { prefix: 'PO' },
  b2b_sequence: { prefix: 'B2B' },
  wa_sequence: { prefix: 'WA' },
  call_in_sequence: { prefix: 'MI' },
};

export const DEM_MASKED_SECRET = '******';

function parseSequenceData(raw: unknown): DemSequenceConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const seq = Number(row.current_seq);
  if (Number.isNaN(seq)) return null;
  return {
    last_period: String(row.last_period ?? ''),
    current_seq: seq,
    prefix: row.prefix != null ? String(row.prefix) : undefined,
  };
}

function parseModules(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item)).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item)).filter(Boolean);
      }
    } catch {
      return [];
    }
  }
  return ['po'];
}

function normalizeDemRole(raw: unknown): DemAppUserRole {
  const value = String(raw ?? 'user').trim().toLowerCase();
  if (value === 'it' || value === 'admin') return value;
  // Legacy supervisor rows display as user for privilege purposes.
  return 'user';
}

async function callDemUsersProxy<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured for DEM user management.');
  }

  const response = await fetch('/dem-users-proxy.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-DEM-EMAIL-KEY': proxyKey,
    },
    body: JSON.stringify({ action, ...payload }),
  });

  const body = (await response.json().catch(() => null)) as { error?: string } | null;
  if (!response.ok) {
    const message = body?.error?.trim() || `User proxy failed (${response.status}).`;
    if (response.status === 401) {
      throw new Error('User proxy unauthorized. Check VITE_DEM_EMAIL_PROXY_KEY and server DEM_EMAIL_PROXY_KEY.');
    }
    if (response.status === 503) {
      throw new Error('User management is not configured on the server (SUPABASE_SERVICE_ROLE_KEY).');
    }
    throw new Error(message);
  }

  return body as T;
}

export async function fetchDemIntegrationConfig(options?: {
  reveal?: boolean;
  actorEmail?: string;
}): Promise<DemIntegrationConfig> {
  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured for integration settings.');
  }

  const reveal = Boolean(options?.reveal);
  const actorEmail = options?.actorEmail?.trim() ?? '';
  if (reveal && !actorEmail) {
    throw new Error('Actor email is required to reveal integration secrets.');
  }

  const headers: Record<string, string> = {
    'X-DEM-EMAIL-KEY': proxyKey,
  };
  if (reveal) {
    headers['X-DEM-Actor-Email'] = actorEmail;
    try {
      // Prefer shared CRM auth client; fall back to DEM schema client.
      const clients = [getDbSupabase('crm'), getDbSupabase('dem')];
      for (const client of clients) {
        const { data } = await client.auth.getSession();
        const token = data.session?.access_token?.trim();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
          // Shared hosts sometimes strip Authorization — mirror on custom header.
          headers['X-DEM-Access-Token'] = token;
          break;
        }
      }
    } catch {
      // Header email remains as fallback when session cannot be read.
    }
  }

  const response = await fetch(
    `/dem-settings-config-proxy.php${reveal ? '?reveal=1' : ''}`,
    {
      method: 'GET',
      headers,
    },
  );

  const body = (await response.json().catch(() => null)) as
    | (DemIntegrationConfig & {
        revealed?: boolean;
        error?: string;
        email?: string;
        reason?: string;
        detail?: string;
      })
    | null;
  if (!response.ok || !body || body.error) {
    const message =
      body?.error?.trim() || `Integration config proxy failed (${response.status}).`;
    const detail = [body?.email, body?.reason, body?.detail].filter(Boolean).join(' · ');
    throw new Error(detail ? `${message} (${detail})` : message);
  }

  const server = body;
  const supabaseUrl = server.supabase.url || import.meta.env.VITE_SUPABASE_URL?.trim() || '';

  // Settings must reflect server secrets only — do not mask Vite build env as if the
  // host has those values (that made empty CloudTalk/anon look like ******).
  return {
    ...server,
    supabase: {
      url: supabaseUrl,
      anon_key: server.supabase.anon_key,
      service_key: server.supabase.service_key,
    },
    cloudtalk: {
      api_id: server.cloudtalk.api_id,
      api_secret: server.cloudtalk.api_secret,
    },
  };
}

export async function fetchDemSequence(key: DemSequenceKey): Promise<DemSequenceConfig | null> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('system_config').select('data').eq('key', key).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  return parseSequenceData(data?.data);
}

export async function fetchAllDemSequences(): Promise<Record<DemSequenceKey, DemSequenceConfig | null>> {
  const db = getDbSupabase('dem');
  const keys: DemSequenceKey[] = ['po_sequence', 'b2b_sequence', 'wa_sequence', 'call_in_sequence'];
  const { data, error } = await db.from('system_config').select('key, data').in('key', keys);
  if (error) throw new Error(formatSupabaseError(error));

  const byKey = new Map<string, unknown>();
  for (const row of data ?? []) {
    const record = row as { key?: string; data?: unknown };
    if (record.key) byKey.set(record.key, record.data);
  }

  return {
    po_sequence: parseSequenceData(byKey.get('po_sequence')),
    b2b_sequence: parseSequenceData(byKey.get('b2b_sequence')),
    wa_sequence: parseSequenceData(byKey.get('wa_sequence')),
    call_in_sequence: parseSequenceData(byKey.get('call_in_sequence')),
  };
}

export async function saveDemSequence(
  key: DemSequenceKey,
  period: string,
  sequence: number,
): Promise<void> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('system_config').select('data').eq('key', key).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));

  const current = parseSequenceData(data?.data);
  const nextData: Record<string, unknown> = {
    ...(current ?? {}),
    current_seq: sequence,
    prefix: current?.prefix ?? SEQUENCE_DEFAULTS[key].prefix,
  };
  if (period.trim()) {
    nextData.last_period = period.trim();
  }

  if (data) {
    const { error: updateError } = await db
      .from('system_config')
      .update({ data: nextData })
      .eq('key', key);
    if (updateError) throw new Error(formatSupabaseError(updateError));
    return;
  }

  const { error: insertError } = await db.from('system_config').insert({
    key,
    data: {
      last_period: period.trim(),
      current_seq: sequence,
      prefix: SEQUENCE_DEFAULTS[key].prefix,
    },
  });
  if (insertError) throw new Error(formatSupabaseError(insertError));
}

export async function saveDemEmailTemplates(templates: DemEmailTemplates): Promise<void> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('system_config').select('key').eq('key', 'email_templates').maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));

  if (data) {
    const { error: updateError } = await db
      .from('system_config')
      .update({ data: templates })
      .eq('key', 'email_templates');
    if (updateError) throw new Error(formatSupabaseError(updateError));
    return;
  }

  const { error: insertError } = await db.from('system_config').insert({
    key: 'email_templates',
    data: templates,
  });
  if (insertError) throw new Error(formatSupabaseError(insertError));
}

export async function fetchDemAppUsers(): Promise<DemAppUser[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('app_users')
    .select('username, role, display_name, accessible_modules')
    .order('username');
  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row) => {
    const record = row as Record<string, unknown>;
    return {
      username: String(record.username ?? ''),
      role: normalizeDemRole(record.role),
      display_name: String(record.display_name ?? record.username ?? ''),
      accessible_modules: parseModules(record.accessible_modules),
    };
  });
}

export async function updateDemAppUserProfile(
  oldUsername: string,
  next: Pick<DemAppUser, 'username' | 'role' | 'display_name' | 'accessible_modules'>,
): Promise<void> {
  const db = getDbSupabase('dem');
  const needsAuthUpdate = oldUsername !== next.username;

  if (needsAuthUpdate) {
    await callDemUsersProxy('update', {
      oldUsername,
      username: next.username,
      role: next.role,
      displayName: next.display_name,
      modules: next.accessible_modules,
    });
    return;
  }

  const { error } = await db
    .from('app_users')
    .update({
      role: next.role,
      display_name: next.display_name,
      accessible_modules: next.accessible_modules,
    })
    .eq('username', oldUsername);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function createDemAppUser(input: {
  username: string;
  password: string;
  role: DemAppUserRole;
  displayName: string;
  modules: string[];
}): Promise<void> {
  await callDemUsersProxy('create', input);
}

export async function deleteDemAppUser(username: string): Promise<void> {
  await callDemUsersProxy('delete', { username });
}

export async function resetDemAppUserPassword(username: string, password: string): Promise<void> {
  await callDemUsersProxy('resetPassword', { username, password });
}

export async function archiveOldDemOrders(): Promise<void> {
  const db = getDbSupabase('dem');
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 60);
  const cutoffIso = cutoff.toISOString();

  // Do not overwrite Cancelled/Void — that would turn voids into "Archived" and inflate Imported KPIs.
  const { error } = await db
    .from('orders')
    .update({ status: 'Archived' })
    .eq('status', 'Processed')
    .lt('created_at', cutoffIso);
  if (error) throw new Error(formatSupabaseError(error));
}
